import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader, CTASection } from "@/components/Shared";
import apiClient from "@/lib/api";
import { Calendar, ArrowRight, ArrowLeft } from "lucide-react";

const CATEGORIES = [
  "all", "Skilled visa updates", "Student visa updates", "PR pathways",
  "Employer sponsorship", "Partner visa tips", "Visitor visa guidance",
  "Refusal and ART updates", "Invitation round updates",
];

export function ResourcesIndex() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get("/blog", { params: category !== "all" ? { category } : {} })
      .then((r) => setPosts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div data-testid="resources-page">
      <PageHeader
        eyebrow="Resources & Insights"
        title="Migration updates, visa insights, and guidance."
        subtitle="Practical articles on skilled migration, student visas, PR pathways, employer sponsorship and ART matters."
        image="/assets/resources.jpg"
      />

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-10 pb-6 border-b border-slate-200">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`resources-cat-${c.replace(/\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 text-xs font-bold tracking-wider uppercase border transition-colors ${
                  category === c
                    ? "bg-[#29324A] text-white border-[#29324A]"
                    : "bg-white text-[#29324A] border-slate-200 hover:border-[#29324A]"
                }`}
              >
                {c === "all" ? "All Posts" : c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20 text-slate-500">Loading...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-500">No posts in this category yet.</p>
              <button onClick={() => setCategory("all")} className="text-[#29324A] font-semibold mt-3">View all posts →</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/resources/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="bg-white group flex flex-col"
                >
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase mb-2">
                      {p.category}
                    </div>
                    <h3 className="font-display text-xl text-[#29324A] mb-3 leading-snug group-hover:text-[#29324A] transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed flex-1 mb-4">{p.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                      <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {new Date(p.created_at).toLocaleDateString("en-AU")}</span>
                      <span className="text-[#29324A] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </div>
  );
}

export function ResourcePost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiClient.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-slate-500">Loading...</div>;
  if (error || !post) return (
    <div className="py-32 text-center">
      <p className="text-slate-500">Post not found.</p>
      <Link to="/resources" className="text-[#29324A] font-semibold mt-3 inline-block">← Back to resources</Link>
    </div>
  );

  return (
    <div data-testid="blog-post-page">
      <article>
        <div className="bg-[#F8F9FA] border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <Link to="/resources" className="text-sm text-[#29324A] font-semibold inline-flex items-center gap-1.5 mb-6"><ArrowLeft className="h-3.5 w-3.5" /> All resources</Link>
            <div className="text-[11px] font-bold tracking-widest text-[#C43A89] uppercase mb-4">{post.category}</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#29324A] leading-tight">{post.title}</h1>
            <div className="mt-6 flex items-center gap-3 text-sm text-slate-500">
              <span className="font-semibold text-[#29324A]">{post.author}</span>
              <span>·</span>
              <span>{new Date(post.created_at).toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" })}</span>
            </div>
          </div>
        </div>

        {post.cover_image && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 mb-12 relative z-10">
            <img src={post.cover_image} alt={post.title} className="w-full aspect-[16/8] object-cover" />
          </div>
        )}

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="prose-studynet" dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>
      </article>
      <CTASection />
    </div>
  );
}
